// Models
import { IModelInput } from 'models'
import { TAction } from 'components/WorkoutModelCard'
import { TModalMoreOption } from 'components/modals/ModalMoreOptions'

// React
import {
  FC,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

// Libraries
import { FormProvider, useForm } from 'react-hook-form'
import { ThemeContext } from 'styled-components'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

// Misc
import { buttonClickTracking } from 'utils/tracking'
import { getDivisionType, getModelName, uid } from 'utils/functions'
import { urls } from 'routes/paths'
import { useMenu, useModal, useToast } from 'hooks'

// Components
import * as Styled from './styled'
import { Body, FormButton, Icon, InputText, SimpleContainer } from 'heeds-ds'
import { HeaderWorkoutRoutine, WorkoutModelCard } from 'components'
import {
  ModalContainer,
  ModalContent,
  ModalHeader,
} from 'components/UI/Containers'
import { ModalMoreOptions, Modal } from 'components/modals'

type Props = {
  addWorkoutModel: (model: IModelInput) => void
  changePage: (page: string) => void
  removeWorkoutModel: (id: string) => void
  selectWorkoutModel: (index: number) => void
  stateModels?: IModelInput[]
  updateWorkoutModel: (id: string, updatedModel: IModelInput) => void
}

type FormFields = {
  index: number
  name: string
}

const WorkoutModelsRoutines: FC<Props> = ({
  addWorkoutModel,
  changePage,
  removeWorkoutModel,
  selectWorkoutModel,
  stateModels,
  updateWorkoutModel,
}) => {
  const { id = '' } = useParams()
  const { openToast } = useToast()
  const { setPagename } = useMenu()
  const { closeModal, openModal, isVisible } = useModal()
  const navigate = useNavigate()
  const theme = useContext(ThemeContext)

  const [selected, setSelected] = useState<number>()

  const formMethods = useForm<FormFields>({
    defaultValues: { index: 0, name: '' },
  })
  const { reset, handleSubmit } = formMethods

  const goToWorkoutModel = useCallback(
    (index: number) => {
      selectWorkoutModel(index)
      changePage('2')
    },
    [changePage, selectWorkoutModel],
  )

  const handleAddButton = useCallback(() => {
    if (stateModels) {
      const routineId = stateModels[0].workout_routine
      addWorkoutModel({
        id: 'NEW' + uid(),
        workout_routine: routineId,
        name: getModelName(
          stateModels.length,
          getDivisionType(stateModels[0].name),
        ),
        workout_set: {},
      })
    }
  }, [addWorkoutModel, stateModels])

  const renameModel = () => {
    if (stateModels && selected !== undefined) {
      const modelSelected = stateModels[selected]
      reset({ name: modelSelected.name, index: selected })
      setSelected(undefined)
      openModal('rename-model-modal')
    }
  }

  const removeModel = () => {
    if (selected !== undefined && stateModels) {
      removeWorkoutModel(stateModels[selected].id)
    }
    closeModal()
    setSelected(undefined)
  }

  const duplicateModel = () => {
    if (selected !== undefined && stateModels) {
      const modelSelected = stateModels?.[selected]
      addWorkoutModel({
        ...modelSelected,
        id: 'NEW' + uid(),
        name: `Cópia de ${modelSelected.name}`,
      })
      closeModal()
      setSelected(undefined)
    }
  }

  const getActions = useCallback(
    (index: number) => {
      const actions: TAction[] = [
        {
          color: theme.colors.interactive.hovered,
          icon: 'moreHorizontal',
          onClick: () => {
            setSelected(index), openModal('modal-more-options-workout-models')
          },
        },
      ]
      return actions
    },
    [openModal, theme.colors.interactive.hovered],
  )

  const handleNavigate = useCallback(() => {
    window.postMessage('CLOSE_WEBVIEW')
    navigate(generatePath(urls.athleteRoutines, { id }), {
      replace: true,
    })
  }, [navigate, id])

  const modalOptions: TModalMoreOption[] = [
    {
      icon: 'contentCopy',
      color: theme.colors.icon.default,
      label: 'Duplicar',
      onClick: duplicateModel,
    },
    {
      icon: 'edit',
      color: theme.colors.icon.default,
      label: 'Renomear Treino',
      onClick: renameModel,
    },
    {
      icon: 'delete',
      color: theme.colors.icon.critical,
      label: 'Excluir rotina',
      onClick: removeModel,
    },
  ]

  const onSubmit = useCallback(
    (formData: FormFields) => {
      if (stateModels) {
        const model = stateModels[formData.index]
        updateWorkoutModel(model.id, { ...model, name: formData.name })
        closeModal()
        openToast({
          type: 'success',
          customTitle: 'Sucesso!',
          message: 'Seu treino foi renomeado.',
        })
      }
    },
    [closeModal, openToast, stateModels, updateWorkoutModel],
  )

  const renderWorkoutModelCard = useMemo(
    () =>
      stateModels?.map((workout, index) => {
        return (
          <WorkoutModelCard
            actions={getActions(index)}
            key={index}
            name={workout.name || ''}
            onClick={() => goToWorkoutModel(index)}
          />
        )
      }),
    [getActions, goToWorkoutModel, stateModels],
  )

  useLayoutEffect(() => {
    setPagename('Treinos')

    return () => setPagename('Dashboard')
  }, [setPagename])

  return (
    <SimpleContainer>
      <HeaderWorkoutRoutine />

      <Styled.Container>
        {renderWorkoutModelCard}

        <Styled.AddButton
          margin={`${stateModels ? '24px' : '190px'} auto`}
          onClick={handleAddButton}
          track={buttonClickTracking}
          trackName="add_new_workout_model"
        >
          <Icon iconName="add" color={theme.colors.interactive.default} />
          Adicionar treino
        </Styled.AddButton>

        <Styled.ButtonContainer>
          <Styled.CancelButton
            onClick={handleNavigate}
            track={buttonClickTracking}
            trackName="navigate_to_athlete_routines"
          >
            Cancelar
          </Styled.CancelButton>

          <Styled.NextButton
            onClick={() => changePage('2')}
            track={buttonClickTracking}
            trackName="navigate_to_mobile_workout_sets_in_workout_create"
          >
            Próximo
          </Styled.NextButton>
        </Styled.ButtonContainer>

        {isVisible === 'rename-model-modal' && (
          <Modal>
            <ModalContainer>
              <ModalHeader>
                <Body
                  color={theme.colors.text.secondary}
                  type="copy1"
                  weight={600}
                >
                  Renomear Treino
                </Body>
              </ModalHeader>

              <ModalContent>
                <FormProvider {...formMethods}>
                  <Styled.Form onSubmit={handleSubmit(onSubmit)}>
                    <InputText
                      label="Nome do treino"
                      margin="0 0 48px"
                      name="name"
                      scale="small"
                    />

                    <FormButton
                      margin="0 auto 0"
                      size="small"
                      track={buttonClickTracking}
                      trackName="updateWorkoutModel"
                    >
                      Salvar
                    </FormButton>
                  </Styled.Form>
                </FormProvider>
              </ModalContent>
            </ModalContainer>
          </Modal>
        )}

        {isVisible === 'modal-more-options-workout-models' && (
          <ModalMoreOptions options={modalOptions} />
        )}
      </Styled.Container>
    </SimpleContainer>
  )
}

export default WorkoutModelsRoutines
